import React from "react";
import { Handle, Position } from "@xyflow/react";
import { Trash2, Workflow, MessageSquare } from "lucide-react";

const basePortStyle = {
  width: 12,
  height: 12,
  border: "2px solid #ffffff",
  borderRadius: "9999px",
  boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.08), 0 2px 4px rgba(15, 23, 42, 0.12)",
};

const baseHandleClass = "cursor-default";
const HANDLE_PREFIX = "btn-";

const handleIdForButton = (btn, fallbackIndex) => {
  const idx =
    typeof btn?.index === "number" && btn.index >= 0 ? btn.index : fallbackIndex;
  return `${HANDLE_PREFIX}${idx}`;
};

function rightPortStyle(color) {
  return {
    ...basePortStyle,
    top: "50%",
    transform: "translateY(-50%)",
    right: -7,
    background: color,
  };
}

export default function CTAFlowNodeBlock({ id, data, selected, onDelete }) {
  const nodeType = data?.templateName ? "template" : "cta_flow";
  
  const Icon = nodeType === "template" ? MessageSquare : Workflow;
  const colorKey = nodeType === "template" ? "emerald" : "purple";
  
  const iconColors = {
    emerald: "text-emerald-600",
    blue: "text-blue-600",
    purple: "text-purple-600",
    amber: "text-amber-600",
    slate: "text-slate-600",
  };

  const headerBg = {
    emerald: "bg-gradient-to-r from-emerald-50/70 to-white",
    blue:    "bg-gradient-to-r from-blue-50/70 to-white",
    purple:  "bg-gradient-to-r from-purple-50/70 to-white",
    amber:   "bg-gradient-to-r from-amber-50/70 to-white",
    slate:   "bg-gradient-to-r from-slate-100/70 to-white",
  };

  const handleColors = {
    emerald: "#10b981",
    blue: "#3b82f6",
    purple: "#a855f7",
    amber: "#f59e0b",
    slate: "#64748b",
  };

  const headerTitle = nodeType === "template" ? "WhatsApp Template" : "CTA Flow";
  const body = data.messageBody || "";
  const templateName = data.templateName || "";
  const buttons = Array.isArray(data.buttons) ? data.buttons : [];

  return (
    <div
      className={`group relative min-h-[116px] w-[260px] max-w-[260px] rounded-lg border bg-white p-0 text-[11px] font-normal text-slate-800 shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] ${
        selected ? "border-emerald-500 ring-2 ring-emerald-500/10 shadow-[0_8px_24px_rgba(16,185,129,0.12)]" : "border-slate-200"
      }`}
    >
      <Handle
        type="target"
        position={Position.Top}
        className={baseHandleClass}
        style={{
          ...basePortStyle,
          top: -8,
          background: handleColors[colorKey] || "#10b981",
        }}
      />

      <div className={`flex items-center justify-between rounded-t-lg pl-4 pr-3 py-3 border-b border-slate-100 ${headerBg[colorKey] || headerBg.slate}`}>
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-slate-200/60">
            <Icon className={`h-3.5 w-3.5 shrink-0 ${iconColors[colorKey] || iconColors.slate}`} strokeWidth={2.5} />
          </div>
          <div className="truncate text-[12px] font-bold tracking-tight text-slate-800">{headerTitle}</div>
        </div>
        {onDelete ? (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onDelete(id);
            }}
            className="inline-flex h-5 w-5 items-center justify-center rounded-md text-slate-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
            title="Delete block"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        ) : null}
      </div>

      <div className="p-3.5 pl-4">
        {templateName && (
          <div className="mb-2 rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-2 text-[11px] font-medium text-slate-600">
            {templateName}
          </div>
        )}

        {body && (
          <div className="ar-node-body-scroll max-h-24 overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-100 bg-slate-50 px-2 py-1.5 pr-1 text-[11px] font-normal leading-relaxed text-slate-700">
            {body}
          </div>
        )}

        {buttons.length > 0 && (
          <div className="mt-2 flex flex-col gap-1.5">
            {buttons.map((btn, index) => (
              <div
                key={index}
                className="relative flex min-h-[26px] items-center justify-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-center text-[11px] font-medium text-slate-700 shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-colors hover:border-emerald-200 hover:bg-emerald-50/30"
              >
                {btn.text || "(unnamed)"}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={handleIdForButton(btn, index)}
                  className={baseHandleClass}
                  style={rightPortStyle(handleColors[colorKey] || "#10b981")}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
